// export const handleShareProduct = (product) => {
//   const navigator = window.navigator;
//   if (!product) return;
//   const category = product?.produtCategory?.parent_category?.slug;
//   const sub_category = product?.produtCategory?.name;
//   const productID = product?.productID;
//   const slug = product?.slug;

//   const shareData = {
//     title: product.productName || 'Check out this product!',
//     text: product.description || 'I found this product interesting!',
//     url: `${window.location.origin}${`/${category}/${sub_category}/${productID}/${slug}`}`,
//   };

//   try {
//     if (navigator.share) {
//       navigator.share(shareData);
//     } else {
//       navigator.clipboard.writeText(shareData.url);
//       alert('Link copied to clipboard!');
//     }
//   } catch (err) {}
// };

import { trackProductShared } from '../../lib/eventTracker';
import { getShareDescription } from '../../utils/stripHtmlAndEntities';

/**
 * Universal property sharing handler for all real estate property types
 * Supports both Rent SmallSmall and Stay SmallSmall properties
 * @param {Object} property - The property object to share
 * @param {Object} user - Optional user object for analytics tracking
 */
export const handleShareProperty = (property, user = null) => {
  if (!property) {
    console.error('No property provided for sharing');
    return;
  }

  const navigator = window.navigator;

  // Detect property type and build appropriate URL
  let baseUrl, shareTitle, shareText, propertyCategory, propertyId;

  // Rent SmallSmall Property (has propertyID and propertyTitle)
  if (property?.propertyID) {
    propertyId = property.propertyID;
    const slug = (property?.propertyTitle || property?.name || '')
      .toLowerCase()
      .replace(/\s+/g, '-');

    baseUrl = `${window.location.origin}/real-estate/rent-smallsmall/${propertyId}/${slug}`;
    shareTitle =
      property?.propertyTitle || property?.name || 'Check out this property!';

    const priceInfo = property?.price
      ? `₦${property.price.toLocaleString()}/month`
      : '';
    const locationInfo = property?.city || property?.location || '';
    const cleanDescription = getShareDescription(property?.description, 100);

    shareText = [shareTitle, priceInfo, locationInfo, cleanDescription]
      .filter(Boolean)
      .join('\n');

    propertyCategory = 'Rent SmallSmall';
  }
  // Stay SmallSmall Property (has apartmentID and apartmentName)
  else if (property?.apartmentID) {
    propertyId = property.apartmentID;
    const slug = (property?.apartmentName || '')
      .toLowerCase()
      .replace(/\s+/g, '-');

    baseUrl = `${window.location.origin}/real-estate/stay-smallsmall/${propertyId}/${slug}`;
    shareTitle = property?.apartmentName || 'Check out this apartment!';

    const priceInfo = property?.price
      ? `₦${property.price.toLocaleString()}`
      : '';
    const locationInfo = property?.location || '';
    const cleanDescription = getShareDescription(property?.description, 100);

    shareText = [shareTitle, priceInfo, locationInfo, cleanDescription]
      .filter(Boolean)
      .join('\n');

    propertyCategory = 'Stay SmallSmall';
  }
  // Fallback - unknown property type
  else {
    console.error('Unknown property type - missing propertyID or apartmentID');
    alert('Unable to share this property');
    return;
  }

  // Build query parameters for rich sharing (optional metadata)
  const params = new URLSearchParams({
    ...(property.price && { price: property.price }),
    ...(property.imageUrl && { image: property.imageUrl }),
    ...(property.images?.gallery?.[0] && { image: property.images.gallery[0] }),
    ...(property.location && { location: property.location }),
    ...(property.city && { city: property.city }),
  });

  const fullUrl = params.toString()
    ? `${baseUrl}?${params.toString()}`
    : baseUrl;

  const shareData = {
    title: shareTitle,
    text: `${shareText}\n\n${fullUrl}`,
  };

  let shareMethod = 'clipboard';

  try {
    // Try native sharing first (mobile devices)
    if (navigator.share) {
      shareMethod = 'native_share';
      navigator
        .share(shareData)
        .then(() => {
          // Track successful share
          trackPropertyShare(
            property,
            user,
            shareMethod,
            fullUrl,
            propertyCategory
          );
        })
        .catch((err) => {
          // User cancelled or sharing failed
          if (err.name !== 'AbortError') {
            console.error('Native share failed:', err);
            fallbackToClipboard(
              shareData.url,
              property,
              user,
              fullUrl,
              propertyCategory
            );
          }
        });
    } else {
      // Fallback to clipboard
      fallbackToClipboard(
        shareData.url,
        property,
        user,
        fullUrl,
        propertyCategory
      );
    }
  } catch (err) {
    console.error('Share failed:', err);
    fallbackToClipboard(
      shareData.url,
      property,
      user,
      fullUrl,
      propertyCategory
    );
  }
};

/**
 * Fallback method to copy URL to clipboard
 */
function fallbackToClipboard(url, property, user, fullUrl, propertyCategory) {
  const shareMethod = 'clipboard';

  navigator.clipboard
    .writeText(url)
    .then(() => {
      alert('Link copied to clipboard!');
      trackPropertyShare(
        property,
        user,
        shareMethod,
        fullUrl,
        propertyCategory
      );
    })
    .catch((err) => {
      console.error('Clipboard write failed:', err);
      // Last resort - show the URL in a prompt
      prompt('Copy this link:', url);
    });
}

/**
 * Track property share event for analytics
 */
function trackPropertyShare(
  property,
  user,
  shareMethod,
  shareUrl,
  propertyCategory
) {
  try {
    trackProductShared({
      productId: property?.propertyID || property?.apartmentID,
      productName:
        property?.propertyTitle || property?.apartmentName || property?.name,
      productPrice: property?.price,
      productCategory: 'Real Estate',
      productSubcategory: propertyCategory,
      shareMethod: shareMethod,
      shareUrl: shareUrl,
      userId: user?.userID || null,
    });
  } catch (err) {
    console.error('Failed to track property share:', err);
  }
}

// Legacy function name for backward compatibility
export const handleShareRSSProperty = handleShareProperty;

export const handleShareProduct = (product, user = null) => {
  const navigator = window.navigator;
  if (!product) return;

  // Handle both produtCategory (typo in backend) and productCategory
  const productCategory = product?.produtCategory || product?.productCategory;

  const category = productCategory?.parent_category?.slug;
  const sub_category = productCategory?.name || productCategory?.slug;
  const productID = product?.productID;
  const slug = product?.slug;

  // If critical fields are missing, show error and exit
  if (!category || !sub_category || !productID) {
    console.error('Product missing required fields for sharing:', {
      category,
      sub_category,
      productID,
      product,
    });
    alert('Unable to share this product. Some information is missing.');
    return;
  }

  const baseUrl = `${window.location.origin}/${category}/${sub_category}/${productID}/${slug || ''}`;

  // Get product image
  const productImage =
    product.imageUrl || product.coverImage || product.mainImage;

  const params = new URLSearchParams({
    name: product.productName || '',
    price:
      product.price || product.fairAppPrice || product.fairAppSalesPrice || '',
    currency: product.currency || '',
    ...(productImage && { image: productImage }),
    ...(product.brand && { brand: product.brand }),
    ...(product.productBrandName && { brand: product.productBrandName }),
    ...(product.condition && { condition: product.condition }),
  });

  const fullUrl = `${baseUrl}?${params.toString()}`;

  const productPrice =
    product.price || product.fairAppPrice || product.fairAppSalesPrice || '';
  const priceText = productPrice
    ? `${product.currency || '₦'}${Number(productPrice).toLocaleString()}`
    : '';

  // Get clean description without HTML tags and entities
  const cleanDescription = getShareDescription(product.description, 250);

  const shareData = {
    title: product.productName || 'Check out this product!',
    text: `${product.productName || 'Product'}${priceText ? ` - ${priceText}` : ''}\n\n${cleanDescription}\n\n${fullUrl}`,
  };

  let shareMethod = 'clipboard';

  // Try to share with image if supported (Web Share API Level 2)
  const shareWithImage = async () => {
    if (!productImage) {
      // No image, proceed with regular share
      return false;
    }

    // Check if sharing files is supported
    if (!navigator.canShare) {
      return false;
    }

    try {
      // Fetch the image and convert to blob
      const response = await fetch(productImage);
      const blob = await response.blob();
      const fileName = `${product.productName?.replace(/[^a-z0-9]/gi, '_') || 'product'}.jpg`;
      const file = new File([blob], fileName, { type: blob.type });

      // Check if we can share this file
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          ...shareData,
          files: [file],
        });
        return true;
      }
    } catch (err) {
      console.log('Sharing with image failed, falling back to URL only:', err);
      return false;
    }

    return false;
  };

  try {
    if (navigator.share) {
      shareMethod = 'native_share';

      // Try sharing with image first
      shareWithImage()
        .then((sharedWithImage) => {
          if (!sharedWithImage) {
            // Fallback to URL-only share
            return navigator.share(shareData);
          }
        })
        .then(() => {
          // Track successful share
          trackProductShared({
            productId: product?.productID,
            productName: product?.productName,
            productPrice:
              product?.fairAppPrice ||
              product?.fairAppSalesPrice ||
              product?.price,
            productCategory: productCategory?.parent_category?.name,
            productSubcategory: productCategory?.name || productCategory?.slug,
            shareMethod: shareMethod,
            shareUrl: fullUrl,
            userId: user?.userID || null,
          });
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareData.url);
      alert('Link copied to clipboard!');

      // Track the share event
      trackProductShared({
        productId: product?.productID,
        productName: product?.productName,
        productPrice:
          product?.fairAppPrice || product?.fairAppSalesPrice || product?.price,
        productCategory: productCategory?.parent_category?.name,
        productSubcategory: productCategory?.name || productCategory?.slug,
        shareMethod: 'clipboard',
        shareUrl: fullUrl,
        userId: user?.userID || null,
      });
    }
  } catch (err) {
    console.error('Share failed:', err);
  }
};
